import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import "moment-timezone";
import useAppDispatch from "hooks/useAppDispatch";
import { fetchAllCountries } from "../../../../redux/features/other/otherSlice";
import {
  activateResend,
  registerUser,
} from "../../../../redux/features/user/userSlice";
import { isEmail, isLatin } from "utils/validation";
import { getApiErrorMessage } from "utils/errors";
import config from "config";
import {
  clearSignUpRecaptchaResponse,
  getSignUpRecaptchaResponse,
  setSignUpRecaptchaResponse,
} from "../utils/signUpRecaptcha";

export default function useSignUpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams();
  const dispatch = useAppDispatch();

  const registered = useSelector((state) => state.user.registered);
  const registering = useSelector((state) => state.user.registering);
  const registerError = useSelector((state) => state.user.registerError);
  const refererLang = useSelector((state) => state.user.refererLang);
  const countries = useSelector((state) => state.other.countriesAll);
  const sent = useSelector((state) => state.user.sent);

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState(false);
  const [name, setName] = useState("");
  const [errorName, setErrorName] = useState(false);
  const [surname, setSurname] = useState("");
  const [errorSurname, setErrorSurname] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [errorCompanyName, setErrorCompanyName] = useState(false);
  const [errorCountry, setErrorCountry] = useState(false);
  const [referralToken, setReferralToken] = useState("");
  const [country, setCountry] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([
    "L`Tax",
    "L`EPR",
    "L`Invoice",
  ]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [registerProjectError, setRegisterProjectError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [rewardfulLoaded, setRewardfulLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchAllCountries());
    if (token) {
      setReferralToken(token);
    }
  }, [dispatch, token]);

  const doRewardfulReady = useCallback(() => {
    window.rewardful("ready", () => {
      setRewardfulLoaded(true);
      if (window.Rewardful?.referral_token) {
        setReferralToken(window.Rewardful.referral_token);
      }
    });
  }, []);

  useEffect(() => {
    if (window.rewardful && !rewardfulLoaded) {
      doRewardfulReady();
    }
  }, [rewardfulLoaded, doRewardfulReady]);

  const isCategoryChecked = useCallback(
    (category) => selectedCategories.includes(category),
    [selectedCategories]
  );

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategories((prev) => {
      if (prev.includes(category)) {
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((item) => item !== category);
      }
      return [...prev, category];
    });
  }, []);

  const doResendEmail = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      dispatch(activateResend({ email }));
    },
    [dispatch, email]
  );

  const validateEmail = useCallback(() => {
    if (email.length > 0 && !isEmail(email)) {
      setErrorEmail(t("signUp.invalidEmail"));
    } else {
      setErrorEmail(false);
    }
  }, [email, t]);

  const handleChange = useCallback((value, id, errorField, errorText) => {
    const fieldMap = {
      email: [setEmail, setErrorEmail],
      name: [setName, setErrorName],
      surname: [setSurname, setErrorSurname],
      companyName: [setCompanyName, setErrorCompanyName],
    };
    const [setValue, setError] = fieldMap[id] || [() => {}, () => {}];

    if (value.length > 0 && isLatin(value)) {
      setValue(value);
      setError(false);
    } else if (value.length > 0 && !isLatin(value)) {
      setValue(value);
      setError("Use the Latin alphabet!");
    } else {
      setValue("");
      setError(errorText);
    }
  }, []);

  const handleChangeCountry = useCallback(
    (nextCountry) => {
      if (nextCountry.length > 0) {
        setCountry(nextCountry);
        setErrorCountry(false);
      } else {
        setCountry("");
        setErrorCountry(t("signUp.enterCountry"));
      }
    },
    [t]
  );

  const handleAppSelect = useCallback((value) => {
    setSelectedApp(value);
  }, []);

  const handleBackToForm = useCallback(() => {
    setSignupError("");
    setRegisterProjectError("");
    setStep(1);
  }, []);

  const handleNextStep = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }

      if (
        !email ||
        !name ||
        !surname ||
        !companyName ||
        !isLatin(name) ||
        !isLatin(surname) ||
        !isLatin(companyName)
      ) {
        const nameErr = isLatin(name)
          ? t("signUp.enterName")
          : "Use the Latin alphabet!";
        const surnameErr = isLatin(surname)
          ? t("signUp.enterSurname")
          : "Use the Latin alphabet!";
        const companyErr = isLatin(companyName)
          ? t("signUp.enterCompany")
          : "Use the Latin alphabet!";

        setErrorEmail(email ? false : t("signUp.invalidEmail"));
        setErrorName(name && isLatin(name) ? false : nameErr);
        setErrorSurname(surname && isLatin(surname) ? false : surnameErr);
        setErrorCompanyName(
          companyName && isLatin(companyName) ? false : companyErr
        );
        return;
      }

      setSignupError("");
      setRegisterProjectError("");
      setStep(2);
    },
    [email, name, surname, companyName, t]
  );

  const doSignup = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
      }

      if (!selectedApp) {
        setRegisterProjectError(
          t("signUp.selectProjectError", "Please select a Lappa App to continue")
        );
        return;
      }

      setRegisterProjectError("");
      setSignupError("");

      const recaptchaResponse = getSignUpRecaptchaResponse();
      if (!recaptchaResponse) {
        return;
      }

      let appValue = 1;
      if (selectedApp === "LTax") appValue = 1;
      else if (selectedApp === "LEPR") appValue = 2;
      else if (selectedApp === "LInvoice") appValue = 3;
      else if (selectedApp === "LAuthorize") appValue = 6;
      else if (selectedApp === "Unknown") appValue = 1;

      const data = {
        email,
        first_name: name,
        last_name: surname,
        "g-recaptcha-response": recaptchaResponse,
        company_name: companyName,
        lang: refererLang,
        referral_token: referralToken,
        app: [appValue],
      };

      if (token) {
        data.token = token;
      }

      dispatch(registerUser(data))
        .then(() => {
          localStorage.removeItem("showGoogleModal");
        })
        .catch((err) => {
          setSignupError(
            getApiErrorMessage(err, t("signUp.connectErr"))
          );
        });
    },
    [
      selectedApp,
      email,
      name,
      surname,
      companyName,
      referralToken,
      refererLang,
      token,
      dispatch,
      t,
    ]
  );

  const inviteErrorMessage = new URLSearchParams(window.location.search).get(
    "inviteError"
  );

  return {
    t,
    navigate,
    step,
    email,
    errorEmail,
    name,
    errorName,
    surname,
    errorSurname,
    companyName,
    errorCompanyName,
    country,
    errorCountry,
    referralToken,
    selectedCategories,
    selectedApp,
    registerProjectError,
    signupError,
    registered,
    registering,
    registerError,
    countries,
    sent,
    inviteErrorMessage,
    recaptchaSitekey: config.recaptcha.sitekey,
    setStep,
    isCategoryChecked,
    handleCategoryChange,
    doResendEmail,
    validateEmail,
    handleChange,
    handleChangeCountry,
    handleAppSelect,
    handleNextStep,
    handleBackToForm,
    doSignup,
    onRecaptchaChange: setSignUpRecaptchaResponse,
    onRecaptchaExpired: clearSignUpRecaptchaResponse,
  };
}
